import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  Divider,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  Assessment as AssessmentIcon,
  ExpandLess,
  ExpandMore,
  Lock as LockIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 240;
const mobileDrawerWidth = 260; // Optimized width for mobile devices
const landscapeDrawerWidth = 220; // Narrower width for landscape mode

const Sidebar = ({ mobileOpen, handleDrawerToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm')); // For very small screens
  // Landscape mode detection - compact mode for devices with limited vertical space
  const isCompactLandscape = useMediaQuery('(orientation: landscape) and (max-height: 500px)');
  // Wider landscape detection for tablets/larger phones
  const isLandscape = useMediaQuery('(orientation: landscape) and (max-height: 600px)');
  const { logout, admin } = useAuth();

  // Set menus to be CLOSED by default
  const [complianceOpen, setComplianceOpen] = useState(false);
  const [operationsOpen, setOperationsOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(
    location.pathname === '/reports' || location.pathname === '/invoice-download'
  );

  const handleComplianceClick = () => {
    setComplianceOpen(!complianceOpen);
  };

  const handleOperationsClick = () => {
    setOperationsOpen(!operationsOpen);
  };

  const handleReportsClick = () => {
    setReportsOpen(!reportsOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile && handleDrawerToggle) {
      handleDrawerToggle();
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/signin');
    if (isMobile && handleDrawerToggle) {
      handleDrawerToggle();
    }
  };

  // Blue Vault theme colors from document
  const sidebarBg = '#4E83BD'; // Primary blue for branding
  const sidebarHeader = '#3A6A99'; // Darker shade of primary blue
  const selectedItem = '#3498DB'; // Bright blue for selected items

  const footerContent = (
    <Box sx={{ flexShrink: 0 }}>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
      {/* Admin Info */}
      {admin && (
        <Box sx={{
          padding: isCompactLandscape ? '8px 16px' : '16px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          display: isCompactLandscape ? 'flex' : 'block',
          alignItems: 'center',
          gap: isCompactLandscape ? '8px' : 0,
        }}>
          <Typography sx={{ fontSize: isCompactLandscape ? '12px' : '13px', fontWeight: 600, mb: isCompactLandscape ? 0 : 0.5 }}>
            {admin.email}
          </Typography>
          <Typography sx={{ fontSize: isCompactLandscape ? '11px' : '12px', opacity: 0.8, textTransform: 'capitalize' }}>
            {admin.role?.replace('_', ' ') || 'Admin'}
          </Typography>
        </Box>
      )}

      {/* Logout Button */}
      <ListItemButton
        onClick={handleLogout}
        sx={{
          padding: isCompactLandscape ? '8px 16px' : '12px 16px',
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.1)',
          }
        }}
      >
        <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
          <LogoutIcon />
        </ListItemIcon>
        <ListItemText
          primary="Logout"
          primaryTypographyProps={{ fontSize: isCompactLandscape ? '14px' : '15px', fontWeight: 500 }}
        />
      </ListItemButton>
    </Box>
  );

  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          padding: !isMobile ? (isLandscape ? '10px 16px' : '16px') : 0,
          paddingTop: !isMobile && isLandscape ? '12px' : (!isMobile ? '16px' : 0), // Fix for desktop centering
          textAlign: 'center',
          backgroundColor: !isMobile ? sidebarHeader : 'transparent', // Transparent on mobile to blend
          borderBottom: !isMobile ? '1px solid rgba(255,255,255,0.1)' : 'none',
          // Spacer height logic:
          minHeight: isCompactLandscape ? '70px' : isMobile ? '70px' : isLandscape ? '44px' : '70px',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '6px',
          flexShrink: 0,
        }}
      >
        {!isMobile && (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              fontSize: isCompactLandscape ? '14px' : isLandscape ? '15px' : '18px',
              lineHeight: 1.2,
            }}
          >
            Meri Zimmedari
          </Typography>
        )}
      </Box>

      <List
        component="nav"
        sx={{
          padding: isCompactLandscape ? '2px 0' : isLandscape ? '4px 0' : '12px 0',
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          // Improve scrolling in landscape mode
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255,255,255,0.1)',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255,255,255,0.3)',
            borderRadius: '2px',
          },
          // Ensure proper scrolling momentum on iOS
          scrollBehavior: 'smooth',
        }}
      >
        {/* Dashboard Item */}
        <ListItemButton
          selected={location.pathname === '/'}
          onClick={() => handleNavigation('/')}
          sx={{
            padding: isCompactLandscape ? '8px 16px' : isLandscape ? '8px 14px' : '12px 16px',
            minHeight: isCompactLandscape ? '48px' : isLandscape ? '40px' : '48px',
            '&.Mui-selected': {
              backgroundColor: selectedItem,
            },
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.1)',
            }
          }}
        >
          <ListItemIcon sx={{ color: 'white', minWidth: isCompactLandscape ? 32 : 40 }}>
            <DashboardIcon fontSize={isCompactLandscape ? 'small' : 'medium'} />
          </ListItemIcon>
          <ListItemText
            primary="Dashboard"
            primaryTypographyProps={{ fontSize: isCompactLandscape ? '13px' : isLandscape ? '14px' : '15px', fontWeight: 500 }}
          />
        </ListItemButton>

        {/* Manage Compliance & FAQ Group */}
        <ListItemButton
          onClick={handleComplianceClick}
          sx={{
            padding: isCompactLandscape ? '8px 16px' : '12px 16px',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.1)',
            }
          }}
        >
          <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
            <LockIcon />
          </ListItemIcon>
          <ListItemText
            primary="Manage Compliance & FAQ"
            primaryTypographyProps={{ fontSize: isCompactLandscape ? '14px' : '15px', fontWeight: 500 }}
          />
          {complianceOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={complianceOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton
              selected={location.pathname === '/policies'}
              onClick={() => handleNavigation('/policies')}
              sx={{
                pl: 8,
                py: isCompactLandscape ? 0.75 : 1.25,
                pr: 2,
                '&.Mui-selected': {
                  backgroundColor: selectedItem,
                },
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                }
              }}
            >
              <ListItemText
                primary="Business Policy"
                primaryTypographyProps={{ fontSize: isCompactLandscape ? '13px' : '14px' }}
              />
            </ListItemButton>
            <ListItemButton
              selected={location.pathname === '/faq'}
              onClick={() => handleNavigation('/faq')}
              sx={{
                pl: 8,
                py: isCompactLandscape ? 0.75 : 1.25,
                pr: 2,
                '&.Mui-selected': {
                  backgroundColor: selectedItem,
                },
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                }
              }}
            >
              <ListItemText
                primary="FAQ"
                primaryTypographyProps={{ fontSize: isCompactLandscape ? '13px' : '14px' }}
              />
            </ListItemButton>
          </List>
        </Collapse>

        {/* Manage Operations Group */}
        <ListItemButton
          onClick={handleOperationsClick}
          sx={{
            padding: isCompactLandscape ? '8px 16px' : '12px 16px',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.1)',
            }
          }}
        >
          <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText
            primary="Manage Operations"
            primaryTypographyProps={{ fontSize: isCompactLandscape ? '14px' : '15px', fontWeight: 500 }}
          />
          {operationsOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={operationsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton
              selected={location.pathname === '/assets'}
              onClick={() => handleNavigation('/assets')}
              sx={{ pl: 8, py: isCompactLandscape ? 0.75 : 1.25, pr: 2, '&.Mui-selected': { backgroundColor: selectedItem }, '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
            >
              <ListItemText primary="Manage Asset List" primaryTypographyProps={{ fontSize: isCompactLandscape ? '13px' : '14px' }} />
            </ListItemButton>
            <ListItemButton
              selected={location.pathname === '/life-declaration'}
              onClick={() => handleNavigation('/life-declaration')}
              sx={{ pl: 8, py: isCompactLandscape ? 0.75 : 1.25, pr: 2, '&.Mui-selected': { backgroundColor: selectedItem }, '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
            >
              <ListItemText primary="Manage Due Life Declaration" primaryTypographyProps={{ fontSize: isCompactLandscape ? '13px' : '14px' }} />
            </ListItemButton>
            <ListItemButton
              selected={location.pathname === '/life-declaration-process'}
              onClick={() => handleNavigation('/life-declaration-process')}
              sx={{ pl: 8, py: isCompactLandscape ? 0.75 : 1.25, pr: 2, '&.Mui-selected': { backgroundColor: selectedItem }, '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
            >
              <ListItemText primary="Manage Life Declaration Process" primaryTypographyProps={{ fontSize: isCompactLandscape ? '13px' : '14px' }} />
            </ListItemButton>
            <ListItemButton
              selected={location.pathname === '/life-declaration-review'}
              onClick={() => handleNavigation('/life-declaration-review')}
              sx={{ pl: 8, py: isCompactLandscape ? 0.75 : 1.25, pr: 2, '&.Mui-selected': { backgroundColor: selectedItem }, '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
            >
              <ListItemText primary="Life Declaration Review" primaryTypographyProps={{ fontSize: isCompactLandscape ? '13px' : '14px' }} />
            </ListItemButton>
            <ListItemButton
              selected={location.pathname === '/death-certificate'}
              onClick={() => handleNavigation('/death-certificate')}
              sx={{ pl: 8, py: isCompactLandscape ? 0.75 : 1.25, pr: 2, '&.Mui-selected': { backgroundColor: selectedItem }, '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
            >
              <ListItemText primary="Manage Death Certificate" primaryTypographyProps={{ fontSize: isCompactLandscape ? '13px' : '14px' }} />
            </ListItemButton>
            <ListItemButton
              selected={location.pathname === '/subscription'}
              onClick={() => handleNavigation('/subscription')}
              sx={{ pl: 8, py: isCompactLandscape ? 0.75 : 1.25, pr: 2, '&.Mui-selected': { backgroundColor: selectedItem }, '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
            >
              <ListItemText primary="Manage Subscription Plan" primaryTypographyProps={{ fontSize: isCompactLandscape ? '13px' : '14px' }} />
            </ListItemButton>
            <ListItemButton
              selected={location.pathname === '/promo-codes'}
              onClick={() => handleNavigation('/promo-codes')}
              sx={{ pl: 8, py: isCompactLandscape ? 0.75 : 1.25, pr: 2, '&.Mui-selected': { backgroundColor: selectedItem }, '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
            >
              <ListItemText primary="Manage Promo Codes" primaryTypographyProps={{ fontSize: isCompactLandscape ? '13px' : '14px' }} />
            </ListItemButton>
            <ListItemButton
              selected={location.pathname === '/communications'}
              onClick={() => handleNavigation('/communications')}
              sx={{ pl: 8, py: isCompactLandscape ? 0.75 : 1.25, pr: 2, '&.Mui-selected': { backgroundColor: selectedItem }, '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
            >
              <ListItemText primary="Manage Communications" primaryTypographyProps={{ fontSize: isCompactLandscape ? '13px' : '14px' }} />
            </ListItemButton>
          </List>
        </Collapse>

        {/* Reports Group */}
        <ListItemButton
          onClick={handleReportsClick}
          sx={{
            padding: isCompactLandscape ? '8px 16px' : '12px 16px',
            backgroundColor: (location.pathname === '/reports' || location.pathname === '/invoice-download') ? 'rgba(255,255,255,0.08)' : 'transparent',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
          }}
        >
          <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
            <AssessmentIcon />
          </ListItemIcon>
          <ListItemText
            primary="Reports"
            primaryTypographyProps={{ fontSize: isCompactLandscape ? '14px' : '15px', fontWeight: 500 }}
          />
          {reportsOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={reportsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton
              selected={location.pathname === '/reports'}
              onClick={() => handleNavigation('/reports')}
              sx={{ pl: 8, py: isCompactLandscape ? 0.75 : 1.25, pr: 2, '&.Mui-selected': { backgroundColor: selectedItem }, '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
            >
              <ListItemText primary="Export Report" primaryTypographyProps={{ fontSize: isCompactLandscape ? '13px' : '14px' }} />
            </ListItemButton>
            <ListItemButton
              selected={location.pathname === '/invoice-download'}
              onClick={() => handleNavigation('/invoice-download')}
              sx={{ pl: 8, py: isCompactLandscape ? 0.75 : 1.25, pr: 2, '&.Mui-selected': { backgroundColor: selectedItem }, '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
            >
              <ListItemText primary="Invoice Download" primaryTypographyProps={{ fontSize: isCompactLandscape ? '13px' : '14px' }} />
            </ListItemButton>
          </List>
        </Collapse>

        {/* Settings */}
        <ListItemButton
          selected={location.pathname === '/settings'}
          onClick={() => handleNavigation('/settings')}
          sx={{
            padding: isCompactLandscape ? '8px 16px' : '12px 16px',
            '&.Mui-selected': {
              backgroundColor: selectedItem,
            },
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.1)',
            }
          }}
        >
          <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText
            primary="Settings"
            primaryTypographyProps={{ fontSize: isCompactLandscape ? '14px' : '15px', fontWeight: 500 }}
          />
        </ListItemButton>
        {isCompactLandscape && footerContent}
      </List>

      {/* User Info and Logout Section */}
      {!isCompactLandscape && footerContent}
    </Box >
  );

  return (
    <Box
      component="nav"
      sx={{
        width: { xs: 0, md: drawerWidth },
        flexShrink: { md: 0 },
      }}
    >
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: isSmallScreen ? '80vw' : mobileDrawerWidth,
            maxWidth: isCompactLandscape ? '280px' : '300px',
            boxSizing: 'border-box',
            backgroundColor: sidebarBg,
            color: 'white',
            height: '100%',
            maxHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: sidebarBg,
            color: 'white',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
